import { useRef } from 'react';
import { MODELOS_POR_MODO, VARIAVEIS, montarTexto } from '@/recepcao/mensagemAgenda';

/**
 * Editor dos textos do modo, com o seletor de qual usar no lote.
 *
 * Os textos ficam na config da clínica para não serem reescritos todo dia. Mas
 * gravar em `clinicas/{id}` é privilégio do gestor — as rules recusam os demais
 * papéis. Por isso a recepcionista edita e envia normalmente, e só o botão de
 * salvar some para ela: bloquear a edição inteira obrigaria a chamar o gestor
 * para ajustar uma vírgula antes de um lote.
 */
/**
 * Os modelos vêm por PROP, não de um import fixo.
 *
 * A tela tem dois modos e cada um tem os seus: confirmação renderiza três
 * textareas, reagendamento uma só. Importar a lista aqui prenderia o componente
 * ao modo de confirmação — que era o único que existia quando ele nasceu.
 */
export default function TextosConfirmacao({
  modelos = MODELOS_POR_MODO.confirmacao,
  textos,
  aoMudarTexto,
  modelo,
  aoMudarModelo,
  dia,
  pessoaPrevia,
  podeSalvar,
  aoSalvar,
  salvando,
  salvo,
  desabilitado,
}) {
  // Um ref por modelo: o chip precisa inserir na posição do cursor, não no fim.
  const refs = useRef({});

  function inserir(campo, chave) {
    const el = refs.current[campo];
    const atual = textos[campo] ?? '';
    if (!el) {
      aoMudarTexto(campo, `${atual}${chave}`);
      return;
    }
    const { selectionStart: ini, selectionEnd: fim } = el;
    aoMudarTexto(campo, `${atual.slice(0, ini)}${chave}${atual.slice(fim)}`);
    // Devolve o foco com o cursor depois da variável, para dar de escrever na
    // sequência sem clicar de novo no campo.
    requestAnimationFrame(() => {
      el.focus();
      const pos = ini + chave.length;
      el.setSelectionRange(pos, pos);
    });
  }

  const campoAtivo = modelos.find((m) => m.id === modelo)?.campo ?? modelos[0].campo;

  return (
    <section className="secao">
      <header className="secao-barra">
        <div>
          <h2 className="secao-titulo">Texto da mensagem</h2>
          <p className="secao-sub">
            Escolha qual deles vai neste lote. As variáveis são trocadas pelos dados de
            cada paciente na hora do envio.
          </p>
        </div>
        {podeSalvar && (
          <div className="item-acoes">
            {salvo && <span className="campo-ajuda">Salvo ✓</span>}
            <button
              type="button"
              className="btn-secundario"
              onClick={aoSalvar}
              disabled={salvando || desabilitado}
            >
              {salvando ? 'Salvando…' : 'Salvar textos'}
            </button>
          </div>
        )}
      </header>

      <div className="cartao">
        {modelos.map(({ id, rotulo, campo }) => {
          const selecionado = modelo === id;
          return (
            <div key={id} className={selecionado ? 'modelo-texto ativo' : 'modelo-texto'}>
              <label className="linha-ajuste">
                <input
                  type="radio"
                  name="modelo-confirmacao"
                  checked={selecionado}
                  onChange={() => aoMudarModelo(id)}
                  disabled={desabilitado}
                />
                <span>
                  <strong>{rotulo}</strong>
                </span>
              </label>

              <textarea
                ref={(el) => {
                  refs.current[campo] = el;
                }}
                rows={4}
                value={textos[campo] ?? ''}
                onChange={(e) => aoMudarTexto(campo, e.target.value)}
                disabled={desabilitado}
                aria-label={rotulo}
              />

              <div className="chips-variaveis">
                {VARIAVEIS.map(({ chave, descricao }) => (
                  <button
                    key={chave}
                    type="button"
                    className="chip-variavel"
                    title={descricao}
                    onClick={() => inserir(campo, chave)}
                    disabled={desabilitado}
                  >
                    {chave}
                  </button>
                ))}
              </div>
            </div>
          );
        })}

        {!podeSalvar && (
          <p className="campo-ajuda">
            Você pode ajustar o texto para este lote. Guardar como padrão da clínica é
            permissão do gestor.
          </p>
        )}
      </div>

      <div className="cartao previa-mensagem">
        <p className="previa-rotulo">
          Prévia — como {pessoaPrevia?.nomeCompleto ? 'o primeiro da lista' : 'um paciente'} vai
          receber
        </p>
        <p className="previa-balao">{montarTexto(textos[campoAtivo], pessoaPrevia, dia)}</p>
      </div>
    </section>
  );
}
